import { isEmpty, omitBy } from 'lodash';
import api from 'src/api';
import logger from 'src/logger';
import type TransactionDocument from 'src/models/transactions/items/transaction-document';
import type { DisplayDocument } from 'src/stores/ui-store';
import { getBlobFromPublicUrl } from './download-blob';
import type { DownloadCfg } from 'src/api/base-api';

export interface DocumentArgs {
  doc?: DisplayDocument;
  transactionDocument?: TransactionDocument;
}

export interface GetDocumentQueryOptions {
  withUrl?: boolean;
}

export function getDocumentQuery(
  { doc = {}, transactionDocument }: DocumentArgs,
  options: GetDocumentQueryOptions = {}
) {
  const {
    tdId,
    tdvId,
    docToken,
    sharedItemTdvId,
    sharedItemUuid,
    actionUuid,
    packageDocumentId,
    transactionId,
    gfpfId,
    isGfpfPreview,
    flowId,
    documentId,
    edId,
  } = doc;

  const { withUrl = true } = options;
  const query = {
    ...(withUrl && {
      url: transactionDocument?.document?.url || doc?.url,
    }),
    tdId: transactionDocument?.id ?? tdId,
    tdvId,
    docToken,
    sharedItemTdvId,
    sharedItemUuid,
    actionUuid,
    packageDocumentId,
    transactionId,
    gfpfId,
    isGfpfPreview,
    flowId,
    documentId,
    edId,
  };
  return omitBy(query, isEmpty);
}

export interface GetDocumentBlobOptions {
  useProxy?: boolean;
  useProxyV2?: boolean;
}

export function getUrl(
  query: DisplayDocument,
  options: GetDocumentBlobOptions = {}
) {
  const cfg = getDownloadCfg(query, options);
  if (!cfg) {
    return null;
  }
  const { url, api } = cfg;
  return {
    apiUrl: api ? `/api${url}` : null,
    nonSecureUrl: api ? null : url,
  };
}

export async function getDocumentBlob(
  query: DisplayDocument,
  options: GetDocumentBlobOptions = {}
) {
  const cfg = getDownloadCfg(query, options);
  if (!cfg) {
    return null;
  }
  return getBlobByCfg(cfg);
}

function getBlobByCfg(cfg: DownloadCfg) {
  const { url, api } = cfg;
  if (api) {
    return api.getBlobByUrl(url);
  }
  return getBlobFromPublicUrl(url);
}

function getDownloadCfg(
  query: DisplayDocument,
  options: GetDocumentBlobOptions = {}
): DownloadCfg | null {
  const {
    url,
    tdId,
    tdvId,
    docToken,
    sharedItemTdvId,
    sharedItemUuid,
    actionUuid,
    packageDocumentId,
    transactionId,
    gfpfId,
    isGfpfPreview,
    flowId,
    documentId,
    edId,
  } = query;

  if (options.useProxy) {
    if (tdId) {
      return api.documents.getDownloadCfgByTdId(tdId);
    }
    if (tdvId) {
      return api.documents.getDownloadCfgByTdvId(tdvId);
    }
    if (flowId && documentId && options.useProxyV2) {
      return api.documents.getFlowAttachmentDownloadCfg(flowId, documentId);
    }
    if (docToken && options.useProxyV2) {
      return api.documents.getDownloadCfgByDocToken(docToken);
    }
    if (transactionId && gfpfId) {
      return api.documents.getDownloadCfgByGfpfId(
        transactionId,
        gfpfId,
        isGfpfPreview ?? false
      );
    }
    if (sharedItemTdvId && sharedItemUuid) {
      return api.sharedPacket.getDocumentDownloadCfg(
        sharedItemUuid,
        sharedItemTdvId
      );
    }
    if (packageDocumentId && actionUuid) {
      return api.packages.getPackageDocumentDownloadCfg(
        actionUuid,
        packageDocumentId
      );
    }
    if (edId) {
      return api.documents.getEnvelopeDocumentDownloadCfg(edId);
    }
    // Do not log an error if the download V2 endpoint is not enabled yet
    if (options.useProxyV2 || (!docToken && !(flowId && documentId))) {
      logger.error(
        'ProxyDownload arguments missing, falling back to default download impl'
      );
    }
  }

  if (!url) {
    return null;
  }

  return {
    url: url,
    api: undefined,
  };
}
